/**
 * QueryClient configuration with global error tracking
 *
 * This client is configured to automatically track all React Query errors
 * to PostHog for observability across data fetching operations.
 */
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { trackSupabaseError, trackEdgeFunctionError } from './analytics/errorHandler';

/**
 * Extract table name from query key
 * Query keys typically look like: ['tableName', id] or ['tableName-suffix', id]
 */
function extractTableFromQueryKey(queryKey: unknown): string {
  if (Array.isArray(queryKey) && queryKey.length > 0) {
    const firstKey = String(queryKey[0]);
    // Remove common suffixes like -list, -detail, -count
    return firstKey.replace(/-list$|-detail$|-count$|-settings$/, '');
  }
  return 'unknown';
}

/**
 * Determine if an error is from an edge function call
 */
function isEdgeFunctionError(error: unknown): boolean {
  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;
    // Edge function errors often have specific structure
    return err.name === 'FunctionsHttpError' ||
           err.name === 'FunctionsRelayError' ||
           err.name === 'FunctionsFetchError';
  }
  return false;
}

/**
 * Extract error details safely
 */
function getErrorDetails(error: unknown): { message: string; code?: string } {
  if (error instanceof Error) {
    return {
      message: error.message,
      code: (error as any).code || (error as any).status?.toString()
    };
  }
  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;
    return {
      message: String(err.message || err.error || 'Unknown error'),
      code: String(err.code || err.status || '')
    };
  }
  return { message: String(error) };
}

/**
 * Create QueryClient with error tracking
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const errorDetails = getErrorDetails(error);
        const table = extractTableFromQueryKey(query.queryKey);

        // Skip tracking for intentional/expected errors
        if (errorDetails.code === 'PGRST116') {
          // "No rows found" - this is often expected, don't track as error
          return;
        }

        // Track the error
        if (isEdgeFunctionError(error)) {
          trackEdgeFunctionError(
            table,
            errorDetails.message,
            undefined // duration not available here
          );
        } else {
          trackSupabaseError('query', table, errorDetails);
        }

        // Log in development
        if (import.meta.env.DEV) {
          console.error(`[QueryClient] Query error for "${table}":`, error);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, variables, context, mutation) => {
        const errorDetails = getErrorDetails(error);

        // Try to extract operation context from mutation key
        const mutationKey = mutation.options.mutationKey;
        const table = mutationKey
          ? extractTableFromQueryKey(mutationKey)
          : 'unknown-mutation';

        // Track the error
        if (isEdgeFunctionError(error)) {
          trackEdgeFunctionError(
            table,
            errorDetails.message,
            undefined
          );
        } else {
          trackSupabaseError('mutation', table, errorDetails);
        }

        // Log in development
        if (import.meta.env.DEV) {
          console.error(`[QueryClient] Mutation error for "${table}":`, error);
        }
      },
    }),
    defaultOptions: {
      queries: {
        // Retry failed queries once (default is 3)
        retry: 1,
        // Don't refetch on window focus in development
        refetchOnWindowFocus: import.meta.env.PROD,
        // Consider data stale after 30 seconds
        staleTime: 30 * 1000,
      },
      mutations: {
        // Don't retry mutations by default
        retry: false,
      },
    },
  });
}

// Export a singleton instance for convenience
export const queryClient = createQueryClient();
